/* Shared video tile behaviour — hero, founder, six creator tiles.

   THREE JOBS, in priority order:

   1. Nothing downloads until visible. The <video> ships with no src (see
      snippets/rb-video-tile.liquid). An IntersectionObserver assigns one and
      calls play() when the tile scrolls in. Eight autoplaying videos on one
      page is a mobile-data and decode problem; this makes it one at a time.

   2. Only the visible one plays. The same observer pauses tiles leaving the
      viewport. Without this, scrolling past the creator rail leaves six
      videos decoding off-screen.

   3. The breakpoint is re-resolved, not resolved once. rb-hero-video.js
      originally picked mobile/desktop at window load, so a rotate or resize
      left the wrong file playing. matchMedia change re-assigns here.

   MUTE is per-tile and starts muted — autoplay requires it. The control is a
   <span role="button">, so Enter and Space have to be wired by hand; a real
   <button> gets no implicit keyboard handling for free either way, and the
   span keeps it clear of MenuDrawer.bindEvents() which binds every <button>
   inside <header-drawer>.

   prefers-reduced-motion: no src is ever assigned. The poster stands alone.
*/
(() => {
  const DESKTOP = window.matchMedia('(min-width: 990px)');
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  const sourceFor = (video) =>
    (DESKTOP.matches ? video.dataset.desktopSrc : video.dataset.mobileSrc) ||
    video.dataset.mobileSrc ||
    video.dataset.desktopSrc ||
    '';

  const ensureSource = (video) => {
    const want = sourceFor(video);
    if (!want) return false;
    /* Compare against the resolved URL, not the attribute — the browser
       normalises src, so a naive !== would reassign on every check and
       restart playback. */
    if (video.dataset.rbSrc !== want) {
      video.dataset.rbSrc = want;
      video.src = want;
      video.load();
    }
    return true;
  };

  const play = (video) => {
    if (REDUCED.matches || video.hasAttribute('data-no-autoplay')) return;
    if (!ensureSource(video)) return;
    const p = video.play();
    /* Autoplay can still be refused (low power mode). Swallow it — the
       poster remains, which is a correct fallback rather than an error. */
    if (p && typeof p.catch === 'function') p.catch(() => {});
  };

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) play(video);
          else if (!video.paused) video.pause();
        });
      }, { rootMargin: '200px 0px', threshold: 0.25 })
    : null;

  const mountTile = (tile) => {
    if (tile.dataset.rbVtMounted) return;
    tile.dataset.rbVtMounted = 'true';

    const video = tile.querySelector('[data-rb-video]');
    const mute = tile.querySelector('[data-rb-video-mute]');
    if (!video) return;

    if (observer) observer.observe(video);
    else play(video);                       // no IO support: just start it

    if (!mute) return;

    const setMuted = (muted) => {
      video.muted = muted;
      mute.setAttribute('aria-pressed', String(muted));
      tile.classList.toggle('rb-vt--unmuted', !muted);
      const base = (mute.getAttribute('aria-label') || '').split(' — ')[0];
      mute.setAttribute('aria-label', base + (muted ? ' — unmute' : ' — mute'));
    };

    const toggle = () => {
      setMuted(!video.muted);
      /* Unmuting is a user gesture, so it is also the moment a refused
         autoplay can be retried. */
      if (!video.muted && video.paused) play(video);
    };

    mute.addEventListener('click', toggle);
    mute.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      event.preventDefault();               // Space would scroll the page
      toggle();
    });
  };

  const mount = () => document.querySelectorAll('.rb-vt').forEach(mountTile);

  /* Re-resolve on breakpoint change: a rotate must swap the file, not keep
     playing the one picked at load. Only touches tiles already started. */
  DESKTOP.addEventListener('change', () => {
    document.querySelectorAll('[data-rb-video]').forEach((video) => {
      if (video.dataset.rbSrc) ensureSource(video);
    });
  });

  document.addEventListener('DOMContentLoaded', mount);
  document.addEventListener('shopify:section:load', mount);
})();
